/**
 * Shared Spatial As-Built assumptions + accuracy disclaimer.
 * Keep identical across order wizards, proposals, and delivery surfaces.
 */

import {
  CUSTOMER_DELIVERABLE_LABELS,
  DELIVERABLE_TYPE_LABELS,
} from "@/lib/virtualspace/deliverable-types";

export const SPATIAL_AS_BUILT_TYPE = "bim_model" as const;

/** Operator / catalog label. */
export const SPATIAL_AS_BUILT_LABEL =
  DELIVERABLE_TYPE_LABELS[SPATIAL_AS_BUILT_TYPE];

/** Customer tracker label. */
export const SPATIAL_AS_BUILT_CUSTOMER_LABEL =
  CUSTOMER_DELIVERABLE_LABELS[SPATIAL_AS_BUILT_TYPE];

export const SPATIAL_AS_BUILT_ASSUMPTIONS_HEADING = "Assumptions";

export const SPATIAL_AS_BUILT_ASSUMPTIONS = [
  "Scanning is limited to line-of-sight visibility. Any materials or items on the floor/ground or obstructing the view of key features should be removed prior to scanning, at no cost to VirtualSpace.",
  "VirtualSpace is not responsible for housekeeping. Any debris, materials, equipment, or other obstructions in the area at the time of scanning will likely be captured in the scan data and could block out potentially needed data.",
  "The client must ensure full access to locations for scanning.",
  "Elevations and architectural features obstructed by vegetation, vehicles, adjacent structures, or inaccessible areas may not be fully captured during scanning.",
] as const;

export const SPATIAL_AS_BUILT_ACCURACY_HEADING = "Accuracy disclaimer";

export const SPATIAL_AS_BUILT_ACCURACY_PARAGRAPHS = [
  "Accuracy considerations for design & planning. Our deliverables are true-to-scan, meaning they reflect the accuracy of the point cloud generated from our scanning process. While spatial LiDAR / capture scans (including Matterport Pro3) typically provide sufficient accuracy for drawings development, factors such as surface reflectivity, environmental conditions, and alignment can affect accuracy.",
  "For design and engineering purposes, we strongly recommend manual field verification of critical dimensions. If higher accuracy is required, our data should be supplemented with survey-grade methods such as total stations or terrestrial LiDAR. VirtualSpace ensures high-quality scans using best practices, but we cannot guarantee capture data for applications requiring millimeter-level accuracy. Clients are responsible for verifying all dimensions before using them for design, permitting, or construction.",
] as const;

const SPATIAL_AS_BUILT_LABEL_NEEDLES = [
  SPATIAL_AS_BUILT_LABEL,
  SPATIAL_AS_BUILT_CUSTOMER_LABEL,
].map((label) => label.toLowerCase());

export function orderIncludesSpatialAsBuilt(
  selectedSlugs: readonly string[] | null | undefined,
): boolean {
  return Boolean(selectedSlugs?.includes(SPATIAL_AS_BUILT_TYPE));
}

/** True when free text / label lists mention Spatial As-Built. */
export function textMentionsSpatialAsBuilt(
  text: string | null | undefined,
): boolean {
  if (!text?.trim()) return false;
  const lower = text.toLowerCase();
  return SPATIAL_AS_BUILT_LABEL_NEEDLES.some((needle) => lower.includes(needle));
}

/**
 * Detect Spatial As-Built on customer proposal surfaces where slugs may
 * already have been mapped to friendly labels (experience model, project
 * scope deliverables row, pricing line labels).
 */
export function proposalIncludesSpatialAsBuilt(args: {
  upgradeSlugs?: readonly string[] | null;
  deliverableLabels?: readonly string[] | null;
  projectScopeDeliverables?: string | null;
  pricingLineLabels?: readonly string[] | null;
}): boolean {
  if (orderIncludesSpatialAsBuilt(args.upgradeSlugs)) return true;
  if (args.deliverableLabels?.some((label) => textMentionsSpatialAsBuilt(label))) {
    return true;
  }
  if (textMentionsSpatialAsBuilt(args.projectScopeDeliverables)) return true;
  if (
    args.pricingLineLabels?.some((label) => textMentionsSpatialAsBuilt(label))
  ) {
    return true;
  }
  return false;
}
